import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {SPACING} from '../../../constants/sizing';
import {Text} from '../../atoms/Text/Text';

interface CoinsRowTileProps {
  ['data-testid']?: string;
  className?: string;
  left?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  right?: React.ReactNode;
}

/**
 * Display-only row tile shared by Coins surfaces (e.g. latest achievements,
 * wallet history transaction preview).
 *
 * The tile is intentionally non-interactive: no tabIndex, no onClick handler,
 * no cursor pointer, no chevron. Consumers that need interactivity wrap the
 * tile externally.
 *
 * Slot layout:
 *   - `left`   visual (image, icon, badge)
 *   - center   title + optional subtitle (takes remaining width, overflows
 *              with ellipsis)
 *   - `right`  optional status indicator or value
 */
export const CoinsRowTile: React.FC<CoinsRowTileProps> = ({
  'data-testid': dataTestId,
  className,
  left,
  title,
  subtitle,
  right,
}) => {
  return (
    <Wrapper className={className} data-testid={dataTestId}>
      {left !== undefined && left !== null && <LeftSlot>{left}</LeftSlot>}
      <CenterSlot>
        <TitleText size="px12" weight="mediumHeavy">
          {title}
        </TitleText>
        {subtitle !== undefined && subtitle !== null && subtitle !== '' && (
          <SubtitleText size="px10" color="Text.Grey" weight="mediumBold">
            {subtitle}
          </SubtitleText>
        )}
      </CenterSlot>
      {right !== undefined && right !== null && <RightSlot>{right}</RightSlot>}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${SPACING['X.Md']};
`;

const LeftSlot = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CenterSlot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  gap: 2px;
`;

const TitleText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${COLORS['Text.Black']};
`;

const SubtitleText = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RightSlot = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
